import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import IPCheckBox from '../../../../../components/IPCheckBox'
import IPSelectBox from '../../../../../components/IPSelectBox'
import Table from '../../../../../components/Table'
import { DEFAULT_EXCLUDE_TEST_DEALERS, DealerTypeFilter } from '../analyticsQuery'
import { exportUsageToExcel } from '../dealerUsageExcel'
import { ADMIN_FEEDBACK_REASONS, adminFeedbackReasonLabel, reasonSortIndex } from '../feedbackReasons'
import { DEFAULT_FEEDBACK_VOTE, FEEDBACK_VOTE_OPTIONS } from '../feedbackResolution'
import {
  FeedbackVoteFilter,
  IChatFeedbackReasonCount,
  IChatFeedbackReport,
  IChatFeedbackRow,
  IFeedbackReportParams,
} from '../models'
import { buildFeedbackSheetRows } from '../reportExcel'
import { DEFAULT_LOAD_ERROR_TEXT, formatCount, formatLocalDateTime, loadErrorMessage, voteIcon } from '../viewModel'
import { UsagePagination } from './DealerUsageTab'
import FeedbackResolveModal, { ResolveFeedbackAction } from './FeedbackResolveModal'
import QuestionAnswerCell from './QuestionAnswerCell'
import { RemoteBadge } from './RemoteBadge'
import styles from './ChatbotAnalytics.module.scss'

const PAGE_SIZE = 50
const EXPORT_PAGE_SIZE = 5000
const SEARCH_DEBOUNCE_MS = 400
const DASH = '—'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'

export type FeedbackReportLoader = (params: IFeedbackReportParams) => Promise<IChatFeedbackReport>

export interface FeedbackReportTabProps {
  fromUtc: Date
  toUtc: Date
  loadFeedbackReport: FeedbackReportLoader
  /** "Deneme Kartı Hariç" (shell'den gelir; Excel dahil her isteğe eklenir). */
  excludeTestDealers?: boolean
  /** "Bayi Tipi" (shell'den gelir; Excel dahil her isteğe eklenir). */
  dealerType?: DealerTypeFilter
  /** Memnuniyetteki sayıya tıklanarak gelindiyse o oy; yoksa `down`. */
  initialVote?: FeedbackVoteFilter
  /** Verilmezse "Sonuçlandır" butonu gösterilmez. */
  resolveFeedback?: ResolveFeedbackAction
  /** Sonuçlandırma başarılı: shell özet sayılarını yeniler. */
  onResolved?: () => void
}

const reasonOptions = [
  { value: '', label: 'Tüm nedenler' },
  ...ADMIN_FEEDBACK_REASONS.map(reason => ({ value: reason.code, label: reason.label })),
]

const reasonCardLabel = (item: IChatFeedbackReasonCount): string =>
  `${adminFeedbackReasonLabel(item.ReasonCode)} · ${formatCount(item.Count)} · ${formatCount(
    item.WithCommentCount
  )} yorumlu`

/**
 * Admin'de geri bildirimler: yorum + bayinin sorusu + Gini'nin cevabı, BAYİ düzeyinde (ADR-021). Varsayılan
 * olumsuz oylar; olumsuz satır "Sonuçlandır" ile Tamamlananlar'a taşınır (Faz-37, ADR-022).
 */
export default function FeedbackReportTab({
  fromUtc,
  toUtc,
  loadFeedbackReport,
  excludeTestDealers = DEFAULT_EXCLUDE_TEST_DEALERS,
  dealerType,
  initialVote = DEFAULT_FEEDBACK_VOTE,
  resolveFeedback,
  onResolved,
}: FeedbackReportTabProps) {
  const [vote, setVote] = useState<FeedbackVoteFilter>(initialVote)
  const [reasonCode, setReasonCode] = useState<string | undefined>(undefined)
  const [onlyWithComment, setOnlyWithComment] = useState(false)
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [textDraft, setTextDraft] = useState('')
  const [text, setText] = useState('')
  const [page, setPage] = useState(1)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  // Sunucunun kullanıcıya dönük hata metni (ör. 90 gün sınırı) panelde gösterilir.
  const [errorMessage, setErrorMessage] = useState(DEFAULT_LOAD_ERROR_TEXT)
  const [report, setReport] = useState<IChatFeedbackReport | null>(null)
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [exporting, setExporting] = useState(false)
  const [resolvingRow, setResolvingRow] = useState<IChatFeedbackRow | null>(null)

  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [])

  // Arama kutuları her tuşta istek atmaz; terim değişince sayfa 1'e döner (DealerUsageTab deseni).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(current => (current === searchDraft ? current : searchDraft))
      setText(current => (current === textDraft ? current : textDraft))
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft, textDraft])

  const currentParams = useCallback(
    (overrides?: Partial<IFeedbackReportParams>): IFeedbackReportParams => ({
      fromUtc,
      toUtc,
      vote,
      reasonCode,
      onlyWithComment,
      search: search.trim() || undefined,
      text: text.trim() || undefined,
      page,
      pageSize: PAGE_SIZE,
      export: false,
      excludeTestDealers,
      dealerType,
      ...overrides,
    }),
    [fromUtc, toUtc, vote, reasonCode, onlyWithComment, search, text, page, excludeTestDealers, dealerType]
  )

  const load = useCallback(async () => {
    const sequence = ++requestSequenceRef.current
    setLoadState('loading')

    try {
      const response = await loadFeedbackReport(currentParams())
      // Geç dönen yanıt yeniyi EZMEZ.
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setReport(response)
      setExpandedRows({})
      setLoadState(response.Page.TotalRows === 0 ? 'empty' : 'ready')
    } catch (error) {
      if (!mountedRef.current || sequence !== requestSequenceRef.current) return
      setErrorMessage(loadErrorMessage(error))
      setLoadState('error')
    }
  }, [currentParams, loadFeedbackReport])

  useEffect(() => {
    load()
  }, [load])

  const selectReason = (code: string | undefined) => {
    setReasonCode(current => (current === code ? undefined : code))
    setPage(1)
  }

  const toggleRow = (index: number) => setExpandedRows(current => ({ ...current, [index]: !current[index] }))

  const exportToExcel = async () => {
    setExporting(true)
    try {
      // Sayfalanmış görünüm değil, aynı filtrelerin TAM sonuç kümesi (en fazla 5000) indirilir.
      const response = await loadFeedbackReport(currentParams({ page: 1, pageSize: EXPORT_PAGE_SIZE, export: true }))
      exportUsageToExcel(
        buildFeedbackSheetRows(response.Page.Rows),
        'chatbot-geri-bildirimler.xlsx',
        'Geri Bildirimler'
      )
    } catch (error) {
      if (mountedRef.current) {
        setErrorMessage(loadErrorMessage(error))
        setLoadState('error')
      }
    } finally {
      if (mountedRef.current) setExporting(false)
    }
  }

  const completeResolution = () => {
    setResolvingRow(null)
    Swal.fire({
      icon: 'success',
      title: 'Geri bildirim sonuçlandırıldı',
      text: 'Kayıt Tamamlananlar sekmesine taşındı.',
      timer: 2500,
      showConfirmButton: false,
    })
    load()
    onResolved?.()
  }

  const reasonCards = useMemo(
    () =>
      [...(report?.ReasonCounts ?? [])].sort(
        (left, right) => reasonSortIndex(left.ReasonCode) - reasonSortIndex(right.ReasonCode)
      ),
    [report]
  )

  const columns = useMemo(
    () => [
      { Header: 'Tarih', accessor: 'date', disableSortBy: true },
      { Header: 'Bayi', accessor: 'dealer', disableSortBy: true },
      { Header: 'Oy', accessor: 'vote', disableSortBy: true },
      { Header: 'Neden', accessor: 'reason' },
      { Header: 'Yorum', accessor: 'comment', disableSortBy: true },
      { Header: 'Soru', accessor: 'question', disableSortBy: true },
      { Header: 'Intent', accessor: 'intent' },
      { Header: 'Sonuç', accessor: 'outcome' },
      { Header: 'İşlem', accessor: 'action', disableSortBy: true },
    ],
    []
  )

  const rows = useMemo(
    () =>
      (report?.Page.Rows ?? []).map((row, index) => {
        const expanded = !!expandedRows[index]
        const clampClass = expanded ? styles.cellFull : styles.cellClamp

        return {
          date: (
            <span className={styles.expandCell}>
              <button
                type='button'
                className={styles.expandButton}
                data-testid={`feedback-expand-${index}`}
                aria-expanded={expanded}
                aria-label={expanded ? 'Satırı daralt' : 'Satırı genişlet'}
                onClick={() => toggleRow(index)}
              >
                {expanded ? '▾' : '▸'}
              </button>
              {formatLocalDateTime(row.VotedAtUtc)}
            </span>
          ),
          dealer: (
            <span>
              {row.DealerName ?? DASH}
              <small className={styles.dealerCode}> {row.DealerSapCode}</small>
              {row.IsRemote ? <RemoteBadge testId={`feedback-row-${index}-remote`} /> : null}
              {/* Yalnız deneme bayisinde sunucu kullanıcı adı / e-posta gönderir (ADR-021 eki). */}
              {(row.UserName || row.UserEmail) && (
                <small className={styles.dealerCode} data-testid={`feedback-row-${index}-user`}>
                  <br />
                  {[row.UserName, row.UserEmail].filter(Boolean).join(' · ')}
                </small>
              )}
            </span>
          ),
          vote: (
            <span data-testid={`feedback-row-${index}-vote`} aria-label={row.Vote === 'up' ? 'Olumlu oy' : 'Olumsuz oy'}>
              {voteIcon(row.Vote)}
            </span>
          ),
          // Olumlu oyda neden/yorum yoktur.
          reason: (
            <span data-testid={`feedback-row-${index}-reason`}>
              {row.Vote === 'up' ? DASH : adminFeedbackReasonLabel(row.ReasonCode)}
            </span>
          ),
          comment: (
            <span className={clampClass} data-testid={`feedback-row-${index}-comment`} title={row.Comment ?? undefined}>
              {row.Comment ?? DASH}
            </span>
          ),
          question: (
            <QuestionAnswerCell
              question={row.Question}
              answer={row.Answer}
              questionSource={row.QuestionSource}
              buttonIntent={row.ButtonIntent}
              conversationDeleted={row.ConversationDeleted}
              expanded={expanded}
              testIdPrefix={`feedback-row-${index}`}
            />
          ),
          intent: row.Intent ?? DASH,
          outcome: row.Outcome ?? DASH,
          // Yalnız OLUMSUZ geri bildirim sonuçlandırılır (Faz-37 §2).
          action:
            row.Vote === 'down' && resolveFeedback ? (
              <button
                type='button'
                className={`${styles.secondaryButton} ${styles.rowActionButton}`}
                data-testid={`feedback-resolve-${index}`}
                onClick={() => setResolvingRow(row)}
              >
                Sonuçlandır
              </button>
            ) : null,
        }
      }),
    // toggleRow her render'da yenidir ama yalnız setState çağırır; bağımlılığa girmesi gerekmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report, expandedRows, resolveFeedback]
  )

  return (
    <section className={styles.page} data-testid='feedback-tab-panel'>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>Geri Bildirimler</h1>
          <p>
            Seçilen aralıkta oy verilen cevapları yorum, soru ve cevapla birlikte inceleyin; olumsuz geri bildirimleri
            sonuçlandırın. Sonuçlandırılanlar Tamamlananlar sekmesindedir.
          </p>
        </div>
        <span className={styles.privacyBadge}>Bayi düzeyinde gösterilir</span>
      </header>

      {report && loadState !== 'error' ? (
        <div className={styles.reasonGrid} role='group' aria-label='Neden dağılımı'>
          {reasonCards.map(item => {
            const code = item.ReasonCode ?? undefined
            const selected = !!code && code === reasonCode
            return (
              <button
                key={item.ReasonCode ?? 'none'}
                type='button'
                data-testid={`feedback-reason-${item.ReasonCode ?? 'none'}`}
                className={`${styles.reasonCard} ${selected ? styles.reasonCardActive : ''}`}
                aria-pressed={selected}
                aria-label={reasonCardLabel(item)}
                // Sunucu "neden yok" filtresi sunmaz (reasonCode kapalı kümeden biri olmalı); kart bilgi amaçlıdır.
                disabled={!code}
                onClick={() => selectReason(code)}
              >
                <span>{adminFeedbackReasonLabel(item.ReasonCode)}</span>
                <strong>{formatCount(item.Count)}</strong>
                <small>{formatCount(item.WithCommentCount)} yorumlu</small>
              </button>
            )
          })}
        </div>
      ) : null}

      <div className={styles.usageToolbar}>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='feedback-vote-filter'
            aria-label='Oy filtresi'
            options={FEEDBACK_VOTE_OPTIONS}
            value={FEEDBACK_VOTE_OPTIONS.find(option => option.value === vote) ?? FEEDBACK_VOTE_OPTIONS[0]}
            onChangeSingle={option => {
              setVote(option?.value ? (String(option.value) as FeedbackVoteFilter) : DEFAULT_FEEDBACK_VOTE)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={styles.toolbarSelect}>
          <IPSelectBox
            id='feedback-reason-filter'
            aria-label='Neden filtresi'
            options={reasonOptions}
            value={reasonOptions.find(option => option.value === (reasonCode ?? '')) ?? reasonOptions[0]}
            onChangeSingle={option => {
              setReasonCode(option?.value ? String(option.value) : undefined)
              setPage(1)
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <IPCheckBox
          id='feedback-only-with-comment'
          label='Yalnız yorumlu'
          checked={onlyWithComment}
          removeMarginBottom
          onCheckedChange={checked => {
            setOnlyWithComment(checked)
            setPage(1)
          }}
        />
        <input
          type='text'
          data-testid='feedback-search'
          className={styles.dateInput}
          value={searchDraft}
          aria-label='Bayi araması'
          placeholder='Bayi ünvanı veya SAP kodu'
          onChange={event => setSearchDraft(event.target.value)}
        />
        <input
          type='text'
          data-testid='feedback-text'
          className={styles.dateInput}
          value={textDraft}
          aria-label='Metin araması'
          placeholder='Yorum veya soru metninde ara'
          onChange={event => setTextDraft(event.target.value)}
        />
        <button
          type='button'
          data-testid='feedback-export'
          className={styles.secondaryButton}
          disabled={exporting}
          onClick={exportToExcel}
        >
          Excel&apos;e Aktar
        </button>
      </div>

      {loadState === 'error' ? (
        <section className={`${styles.statePanel} ${styles.errorState}`} role='alert' data-testid='feedback-error'>
          <span className={styles.stateIcon} aria-hidden='true'>
            !
          </span>
          <h2>Geri bildirimler yüklenemedi</h2>
          <p>{errorMessage}</p>
        </section>
      ) : null}

      {loadState === 'empty' ? (
        <section className={styles.statePanel} role='status' data-testid='feedback-empty'>
          <span className={styles.stateIcon} aria-hidden='true'>
            —
          </span>
          <h2>Henüz veri yok</h2>
          <p>Seçilen aralıkta ve filtrelerde geri bildirim bulunamadı.</p>
        </section>
      ) : null}

      {loadState === 'loading' ? (
        <section className={styles.statePanel} role='status' aria-live='polite' data-testid='feedback-loading'>
          <span className={styles.loadingMark} aria-hidden='true' />
          <h2>Geri bildirimler yükleniyor</h2>
        </section>
      ) : null}

      {report && loadState === 'ready' ? (
        <>
          <div className={styles.reportTable}>
            <Table columns={columns} data={rows} initialPageSize={PAGE_SIZE} hidePagination />
          </div>
          <UsagePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={report.Page.TotalRows}
            onPageChange={setPage}
            testIdPrefix='feedback'
          />
        </>
      ) : null}

      {resolvingRow && resolveFeedback ? (
        <FeedbackResolveModal
          row={resolvingRow}
          canAnnounce={report?.CanAnnounce === true}
          resolveFeedback={resolveFeedback}
          onCancel={() => setResolvingRow(null)}
          onResolved={completeResolution}
        />
      ) : null}
    </section>
  )
}
